import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  HStack,
  Stack,
} from '@chakra-ui/react';
import { ClipboardListIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  SubscriptionWorkflowType,
  useGetBankAccountByCompanyIdQuery,
  useGetFundraisingQuery,
  useReviewFundraisingMutation,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast } from '@blockpulse3/ui/commons';

type Props = {
  backHref?: string;
};

/**
 * FundraisingView.
 * View of a Fundraising.
 *
 * @returns {JSX.Element}
 */
export function FundraisingStatusAlert({ backHref }: Props): JSX.Element | null {
  const i18nFundraising = useTranslations('Fundraising');

  const errorToast = useErrorToast();

  const { fundraisingId = '' } = useParams();

  const { data, loading, refetch } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
  });
  const fundraising = data?.fundraising;

  const { data: bankAccount, loading: loadingBankAccount } = useGetBankAccountByCompanyIdQuery({
    variables: {
      companyId: fundraising?.company.id || '',
    },
    skip: !fundraising || fundraising?.isExternalIBAN,
  });

  const [reviewFundraising, { loading: reviewLoading }] = useReviewFundraisingMutation();

  const handleReviewFundraising = (): void => {
    reviewFundraising({
      variables: {
        fundraisingId,
      },
      onCompleted: () => {
        refetch();
      },
      onError: (err: unknown) => {
        const error = err as Error;
        if (error.message === Exceptions.CannotReviewedHardCapExceeded) {
          errorToast({
            title: i18nFundraising('Toast.CannotReviewedHardCapExceeded'),
          });
        }
      },
    });
  };

  if (!fundraising || loading || loadingBankAccount) return null;

  const isBankAccountAvailable = fundraising?.isExternalIBAN || bankAccount;
  const isHardCapExceeded =
    fundraising.status === FundraisingStatus.CLOSED &&
    fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;

  if (!isBankAccountAvailable) {
    return (
      <Alert alignItems="center" status="warning">
        <AlertIcon boxSize="16px" />
        <AlertTitle>{i18nFundraising('BankAccountCreationPending')}</AlertTitle>
      </Alert>
    );
  }

  if (isHardCapExceeded) {
    return (
      <Alert boxShadow="md" status="warning">
        <Stack
          alignItems="stretch"
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing="4"
          width="full"
        >
          <HStack alignItems="flex-start" spacing="0">
            <AlertIcon as={ClipboardListIcon} />
            <Stack spacing="0">
              <AlertTitle fontWeight="bold">{i18nFundraising('FundraisingToReview')}</AlertTitle>
              <AlertDescription>
                {i18nFundraising('FundraisingToReviewDescription')}
              </AlertDescription>
            </Stack>
          </HStack>
          <HStack alignItems="center" ml={{ base: '0', md: '4' }}>
            <Button
              className="confirm"
              fontSize="sm"
              isDisabled={reviewLoading}
              isLoading={reviewLoading}
              onClick={handleReviewFundraising}
            >
              {i18nFundraising('ReviewDone')}
            </Button>
          </HStack>
        </Stack>
      </Alert>
    );
  }

  return null;
}

export type FundraisingViewProps = Props;
